import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AccountType, BASE_URL_TOKEN } from '@raptor-fe/core';

/**
 * API service for managing AccountType entities.
 * AccountTypes are tenant-level configuration for billing types (Cash, Fleet, Local Account, etc.).
 *
 * Note: AccountType is tenant-scoped (no branchId), unlike most other entities.
 */
@Injectable({
    providedIn: 'root',
})
export class AccountTypeApiService {
    private readonly http = inject(HttpClient);
    private readonly baseUrl = inject(BASE_URL_TOKEN);

    /**
     * Get all account types for a tenant.
     * @param tenantId - The tenant ID
     * @returns Observable of AccountType array
     */
    getAll(tenantId: string): Observable<AccountType[]> {
        return this.http.get<AccountType[]>(this.getUrl(tenantId));
    }

    /**
     * Get a specific account type by ID.
     * @param tenantId - The tenant ID
     * @param id - The account type ID (e.g., 'cash', 'fleet')
     * @returns Observable of AccountType
     */
    getById(tenantId: string, id: string): Observable<AccountType> {
        return this.http.get<AccountType>(this.getUrl(tenantId, id));
    }

    /**
     * Create a new account type (TenantAdmin only).
     * @param tenantId - The tenant ID
     * @param accountType - The account type to create
     * @returns Observable of created AccountType
     */
    create(tenantId: string, accountType: AccountType): Observable<AccountType> {
        return this.http.post<AccountType>(this.getUrl(tenantId), accountType);
    }

    /**
     * Update an existing account type (TenantAdmin only).
     * @param tenantId - The tenant ID
     * @param accountType - The account type to update
     * @returns Observable of updated AccountType
     */
    update(tenantId: string, accountType: AccountType): Observable<AccountType> {
        return this.http.put<AccountType>(this.getUrl(tenantId), accountType);
    }

    /**
     * Delete an account type (TenantAdmin only).
     * @param tenantId - The tenant ID
     * @param id - The account type ID to delete
     * @returns Observable of void
     */
    delete(tenantId: string, id: string): Observable<void> {
        return this.http.delete<void>(this.getUrl(tenantId, id));
    }

    /**
     * Build the URL for AccountType API endpoints.
     * @param tenantId - The tenant ID
     * @param path - Optional path segment (e.g., account type ID)
     * @returns Full URL string
     */
    private getUrl(tenantId: string, path = ''): string {
        const base = `${this.baseUrl}v1/tenant/${tenantId}/accounttype`;
        return path ? `${base}/${path}` : base;
    }
}
